/**
 * A state transition view as React Flow nodes and edges. No React, no DOM.
 *
 * Kept apart from the canvas for the reason `diagram/layout.ts` is: a sub-state
 * placed relative to the wrong parent renders perfectly and is wrong, and only
 * arithmetic asserted directly catches that.
 *
 * Two coordinate systems meet here. The layout file stores ABSOLUTE boxes, so a
 * box survives its machine being moved or removed from the view; React Flow
 * positions a child RELATIVE to its parent. Everything converting between them
 * is in this file.
 */

import { Position } from '@xyflow/react'
import type { Edge, Node } from '@xyflow/react'
import type { LayoutBox, Transition, ViewState } from '../../schema'
import { parallelOffsets } from './edgeGeometry'

export interface StateNodeData extends Record<string, unknown> {
  /** The model identifier, which is what the rest of the API addresses a state by. */
  identifier: string
  label: string
  statement: string
  isInitial: boolean
  /** True when the view shows at least one state declared inside this one. */
  isMachine: boolean
  /** What a screen reader calls it: 'State machine', 'Step', and so on. */
  kindLabel: string
  /** A `start` or `done` node, which stands for no element. */
  pseudo?: 'start' | 'done'
}

/** What a diagram calls a box that contains others, and one that does not. */
export interface BoxLabels {
  container: string
  leaf: string
  /** A box inside nothing and holding nothing, when that differs from `leaf`: a function is not a step. */
  top?: string
}

const STATE_LABELS: BoxLabels = { container: 'State machine', leaf: 'State' }

export interface TransitionEdgeData extends Record<string, unknown> {
  /** The state whose body declares the transition - the one a write goes to. */
  owner: string
  /** Its position in that body, which is its only identity. */
  index: number
  transition: Transition
  /** How far to bow it off the straight line, so parallel edges do not lie on each other. */
  offset: number
}

export type StateNode = Node<StateNodeData, 'state'>
export type TransitionEdge = Edge<TransitionEdgeData>

/** A leaf state, before anyone has sized it. */
export const LEAF_SIZE = { width: 168, height: 60 }
/** A `start` or `done` node: a mark, not a box. */
export const PSEUDO_SIZE = { width: 28, height: 28 }

/** The size a box starts at, before anyone has sized it. */
export function initialSize(state: Pick<ViewState, 'pseudo'>): { width: number; height: number } {
  return state.pseudo ? PSEUDO_SIZE : LEAF_SIZE
}
/** Room inside a machine: the title strip above its states, and a margin round them. */
export const MACHINE_HEADER = 40
export const MACHINE_PADDING = 24

/** `trigger [guard] / effect`, each part only when stated - the UML reading order. */
export function transitionLabel(transition: Transition): string {
  const parts = []
  if (transition.trigger) parts.push(transition.trigger)
  if (transition.guard) parts.push(`[${transition.guard}]`)
  const head = parts.join(' ')
  return transition.effect ? `${head}${head ? ' ' : ''}/ ${transition.effect}` : head
}

/**
 * Every shown state's parent, as a qualified name, or null at the top.
 *
 * The owner on the wire is an IDENTIFIER, and the parent a node hangs from has to
 * be one of the nodes. A machine the view does not show is no parent at all, so
 * its states are drawn at the top rather than dropped.
 */
export function parentsOf(states: ViewState[]): Map<string, string | null> {
  const byIdentifier = new Map(states.map((state) => [state.id, state.qualified_name]))
  const parents = new Map<string, string | null>()
  for (const state of states) {
    const parent = state.owner ? byIdentifier.get(state.owner) ?? null : null
    parents.set(state.qualified_name, parent === state.qualified_name ? null : parent)
  }
  return parents
}

/**
 * Shown states ordered so every parent precedes its children, which React Flow
 * requires of a sub-flow. Declaration order is kept within a level.
 */
export function parentFirst(states: ViewState[]): ViewState[] {
  const parents = parentsOf(states)
  const depth = (name: string, seen = new Set<string>()): number => {
    const parent = parents.get(name)
    if (!parent || seen.has(parent)) return 0
    seen.add(name)
    return 1 + depth(parent, seen)
  }
  return states
    .map((state, index) => ({ state, index, depth: depth(state.qualified_name) }))
    .sort((a, b) => a.depth - b.depth || a.index - b.index)
    .map((entry) => entry.state)
}

/**
 * The nodes for a view, placed from ABSOLUTE boxes.
 *
 * A state with no box is still returned, at the origin, and named in `missing`
 * so the caller can have it placed. It is never left out: a diagram that drops
 * what it cannot place shows a tidier model than the one that exists.
 */
export function stateNodes(
  states: ViewState[],
  boxes: Record<string, LayoutBox>,
  labels: BoxLabels = STATE_LABELS,
): { nodes: StateNode[]; missing: string[] } {
  const parents = parentsOf(states)
  const machines = new Set([...parents.values()].filter((name): name is string => !!name))
  const missing: string[] = []

  const nodes = parentFirst(states).map((state): StateNode => {
    const name = state.qualified_name
    const box = boxes[name]
    if (!box) missing.push(name)
    const parent = parents.get(name) ?? null
    const parentBox = parent ? boxes[parent] : undefined
    const width = box?.width ?? initialSize(state).width
    const height = box?.height ?? initialSize(state).height
    return {
      id: name,
      type: 'state',
      position: {
        x: (box?.x ?? 0) - (parentBox?.x ?? 0),
        y: (box?.y ?? 0) - (parentBox?.y ?? 0),
      },
      ...(parent ? { parentId: parent } : {}),
      width,
      height,
      // Stated up front, with the two handles StateNodeView draws, so edges can
      // be drawn before the browser has measured anything - React Flow will not
      // draw an edge between nodes it has no handle positions for. The real
      // measurement replaces both on first render.
      measured: { width, height },
      handles: [
        { id: null, type: 'target', position: Position.Left, x: 0, y: height / 2, width: 1, height: 1 },
        { id: null, type: 'source', position: Position.Right, x: width, y: height / 2, width: 1, height: 1 },
      ],
      style: { width, height },
      data: {
        identifier: state.id,
        label: state.name || state.id,
        statement: state.statement,
        isInitial: state.is_initial,
        isMachine: machines.has(name),
        kindLabel: machines.has(name) ? labels.container : parent ? labels.leaf : (labels.top ?? labels.leaf),
        ...(state.pseudo ? { pseudo: state.pseudo } : {}),
      },
    }
  })
  return { nodes, missing }
}

/**
 * One edge per transition whose two ends the view shows.
 *
 * An end the view does not show - or one that names a state nothing declares -
 * drops that edge and COUNTS it, so the canvas can say how many it is not
 * drawing rather than appear to show the whole machine.
 */
export function transitionEdges(states: ViewState[]): { edges: TransitionEdge[]; hidden: number } {
  const byIdentifier = new Map(states.map((state) => [state.id, state.qualified_name]))
  const edges: TransitionEdge[] = []
  let hidden = 0
  for (const state of states) {
    state.transitions.forEach((transition, index) => {
      const source = byIdentifier.get(transition.from)
      const target = byIdentifier.get(transition.to)
      if (!source || !target) {
        hidden += 1
        return
      }
      edges.push({
        id: `${state.qualified_name}#${index}`,
        type: 'transition',
        source,
        target,
        label: transitionLabel(transition),
        // Above every node: an edge between two sub-states is otherwise drawn
        // under their machine's box.
        zIndex: 1000,
        data: { owner: state.id, index, transition, offset: 0 },
      })
    })
  }
  const offsets = parallelOffsets(edges)
  for (const edge of edges) {
    if (edge.data) edge.data.offset = offsets[edge.id] ?? 0
  }
  return { edges, hidden }
}

/**
 * The absolute box of every node, for saving.
 *
 * Reads the size React Flow MEASURED when it has one, since a resize changes
 * that and not the declared width. Rounded to whole units, as the server does.
 */
export function boxesFromNodes(nodes: Node[]): Record<string, LayoutBox> {
  const byId = new Map(nodes.map((node) => [node.id, node]))
  const absolute = (node: Node, seen = new Set<string>()): { x: number; y: number } => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined
    if (!parent || seen.has(node.id)) return { ...node.position }
    seen.add(node.id)
    const origin = absolute(parent, seen)
    return { x: origin.x + node.position.x, y: origin.y + node.position.y }
  }
  const boxes: Record<string, LayoutBox> = {}
  for (const node of nodes) {
    const { x, y } = absolute(node)
    boxes[node.id] = {
      x: Math.round(x),
      y: Math.round(y),
      width: Math.round(node.measured?.width ?? node.width ?? LEAF_SIZE.width),
      height: Math.round(node.measured?.height ?? node.height ?? LEAF_SIZE.height),
    }
  }
  return boxes
}

/** Between a placed state and the one below it. */
export const PLACEMENT_GAP = 32

/**
 * Boxes for the states that have none, placed around the ones that do.
 *
 * NOT an arrangement. Arranging a whole view to place one new state put it
 * exactly where an existing state already sat - the fresh layout gave it that
 * slot, and the existing state kept its stored box - so one covered the other.
 * Here a new state goes BELOW its placed siblings, or into its machine's top
 * corner if it has none, and every machine round it grows to hold it.
 *
 * @param states every state the view shows
 * @param known the boxes already placed; not changed
 */
export function placeMissing(states: ViewState[], known: Record<string, LayoutBox>): Record<string, LayoutBox> {
  const boxes: Record<string, LayoutBox> = { ...known }
  const parents = parentsOf(states)

  for (const state of parentFirst(states)) {
    const name = state.qualified_name
    if (boxes[name]) continue
    const parent = parents.get(name) ?? null
    const siblings = states
      .filter((other) => other.qualified_name !== name && (parents.get(other.qualified_name) ?? null) === parent)
      .map((other) => boxes[other.qualified_name])
      .filter((box): box is LayoutBox => !!box)
    const parentBox = parent ? boxes[parent] : undefined

    let x = 0
    let y = 0
    if (siblings.length) {
      x = Math.min(...siblings.map((box) => box.x))
      y = Math.max(...siblings.map((box) => box.y + box.height)) + PLACEMENT_GAP
    } else if (parentBox) {
      x = parentBox.x + MACHINE_PADDING
      y = parentBox.y + MACHINE_HEADER
    }
    boxes[name] = { x, y, ...initialSize(state) }

    // Grow each enclosing machine to hold what is now inside it - and push
    // aside whatever the growth now covers. Found on screen: a step added to a
    // function grew it over a function placed beside it.
    let child = name
    let around = parents.get(child) ?? null
    while (around && boxes[around]) {
      const inner = boxes[child]
      const before = boxes[around]
      boxes[around] = {
        ...before,
        width: Math.max(before.width, inner.x + inner.width + MACHINE_PADDING - before.x),
        height: Math.max(before.height, inner.y + inner.height + MACHINE_PADDING - before.y),
      }
      makeRoom(states, parents, boxes, around, before)
      child = around
      around = parents.get(child) ?? null
    }
  }
  return boxes
}

const overlap = (a: LayoutBox, b: LayoutBox) =>
  a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height

/**
 * Move the boxes beside a grown one that it now covers and did not before.
 *
 * Only boxes at the SAME level - what is inside the grown box is meant to be
 * covered by it. A box moves right if it began right of the grown box's old
 * edge, and down otherwise; everything inside it moves with it. A push that
 * lands on a third box pushes that one too - found on screen, where a column of
 * functions was pushed into itself - and every push moves strictly away, so
 * the chain ends; `depth` is only a guard.
 */
function makeRoom(
  states: ViewState[],
  parents: Map<string, string | null>,
  boxes: Record<string, LayoutBox>,
  grown: string,
  before: LayoutBox,
  depth = 0,
) {
  if (depth > states.length) return
  const level = parents.get(grown) ?? null
  const after = boxes[grown]
  for (const other of states) {
    const name = other.qualified_name
    const box = boxes[name]
    if (name === grown || !box || (parents.get(name) ?? null) !== level) continue
    if (!overlap(after, box) || overlap(before, box)) continue
    const dx = box.x >= before.x + before.width ? after.x + after.width + PLACEMENT_GAP - box.x : 0
    const dy = dx ? 0 : after.y + after.height + PLACEMENT_GAP - box.y
    for (const moved of states) {
      const key = moved.qualified_name
      if (boxes[key] && (key === name || key.startsWith(`${name}::`))) {
        boxes[key] = { ...boxes[key], x: boxes[key].x + dx, y: boxes[key].y + dy }
      }
    }
    makeRoom(states, parents, boxes, name, box, depth + 1)
  }
}

/**
 * Stored boxes win over placed ones, for every element that has one.
 *
 * The rule decision 012 turns on: arranging never silently moves a box somebody
 * placed. Only what has no stored box takes the arranged position.
 */
export function mergeBoxes(
  stored: Record<string, LayoutBox>,
  arranged: Record<string, LayoutBox>,
): Record<string, LayoutBox> {
  return { ...arranged, ...stored }
}
